import React, { useState, useEffect } from 'react';
import PrinterConfig from '../forms/PrinterConfig';
import ScaleConfig from '../forms/ScaleConfig';
import CameraConfig from '../forms/CameraConfig';

const ConfigScreen = ({ config, setConfig, devices, onConnect, onNavigate, company, setCompany }) => {
	const [cameras, setCameras] = useState(config.cameras || []);
	const [saving, setSaving] = useState(false);
	const [companies, setCompanies] = useState([]);
	const [locations, setLocations] = useState([]);
	const [locationsLoading, setLocationsLoading] = useState(false);
	const [selectedCompanyLocation, setSelectedCompanyLocation] = useState(config.selectedCompanyLocation || '');
	const [selectedCompany, setSelectedCompany] = useState(config.selectedCompany || '');
	const [selectedLocation, setSelectedLocation] = useState(config.selectedLocation || '');

	// Update local state when config prop changes
	useEffect(() => {
		if (config.cameras) {
			setCameras(config.cameras);
		}
		if (config.selectedCompanyLocation) {
			setSelectedCompanyLocation(config.selectedCompanyLocation);
		}
		if (config.selectedCompany) {
			setSelectedCompany(config.selectedCompany);
		}
		if (config.selectedLocation) {
			setSelectedLocation(config.selectedLocation);
		}
	}, [config]);

	// Fetch companies and locations when component mounts
	useEffect(() => {
		fetchCompaniesAndLocations();
	}, []);

	// Load saved configuration from secure storage (only for location)
	useEffect(() => {
		loadSavedLocation();
	}, []);

	const fetchCompaniesAndLocations = async () => {
		setLocationsLoading(true);
		try {
			// Fetch all companies for the user
			const companiesResponse = await window.electronAPI.request(
				'/companies',
				'GET',
				null,
				{
					'Accept': 'application/json',
				}
			);

			if (companiesResponse.ok) {
				const companiesData = await companiesResponse.body;
				setCompanies(companiesData || []);

				// Fetch locations for each company
				const allLocations = [];
				for (const companyItem of companiesData || []) {
					try {
						const locationsResponse = await window.electronAPI.request(
							`/companies/${companyItem.Id}/locations`,
							'GET',
							null,
							{
								'Accept': 'application/json',
							}
						);

						if (locationsResponse.ok) {
							const companyLocations = await locationsResponse.body;
							// Add company info to each location
							const locationsWithCompany = (companyLocations || []).map(location => ({
								...location,
								CompanyId: companyItem.Id,
								CompanyName: companyItem.Name || companyItem.CompanyName,
								DisplayName: `${companyItem.Name || companyItem.CompanyName} - ${location.SiteName}`
							}));
							allLocations.push(...locationsWithCompany);
						}
					} catch (error) {
						console.error(`Error fetching locations for company ${companyItem.Id}:`, error);
					}
				}
				setLocations(allLocations);
			} else {
				console.error('Failed to fetch companies');
			}
		} catch (error) {
			console.error('Error fetching companies and locations:', error);
		} finally {
			setLocationsLoading(false);
		}
	};

	const loadSavedLocation = async () => {
		try {
			// Load company from secureStorage.getCompany()
			if (window.electronAPI?.secureStorage?.getCompany) {
				const savedCompany = await window.electronAPI.secureStorage.getCompany();
				// console.log('🔍 Loaded company from secureStorage:', savedCompany);
				if (savedCompany?.Id) {
					setSelectedCompany(savedCompany.Id);
				}
			}

			// Load location from secureStorage.getSettings()
			if (window.electronAPI?.secureStorage?.getSettings) {
				const savedSettings = await window.electronAPI.secureStorage.getSettings();
				// console.log('🔍 Loaded settings from secureStorage:', savedSettings);
				if (savedSettings?.selectedLocation) {
					setSelectedLocation(savedSettings.selectedLocation);
					// Set the company-location combination for the dropdown
					setSelectedCompanyLocation(savedSettings.selectedLocation);
				}
			} else {
				// Fallback to localStorage
				const stored = localStorage.getItem('truckscale_settings');
				if (stored) {
					const savedSettings = JSON.parse(stored);
					// console.log('🔍 Loaded settings from localStorage:', savedSettings);
					if (savedSettings?.selectedLocation) {
						setSelectedLocation(savedSettings.selectedLocation);
						setSelectedCompanyLocation(savedSettings.selectedLocation);
					}
				}
			}
		} catch (error) {
			console.error('Failed to load saved location:', error);
		}
	};

	const addCamera = () => {
		const newCamera = {
			id: Date.now(),
			name: '',
			mjpegUrl: '',
			snapshotUrl: '',
			authType: 'none',
			username: '',
			password: '',
			isPrimary: cameras.length === 0
		};
		setCameras([...cameras, newCamera]);
	};

	const updateCamera = (id, field, value) => {
		setCameras(cameras.map(cam =>
			cam.id === id ? { ...cam, [field]: value } : cam
		));
	};

	const removeCamera = (id) => {
		setCameras(cameras.filter(cam => cam.id !== id));
	};

	const handleCompanyLocationChange = (companyLocationId) => {
		setSelectedCompanyLocation(companyLocationId);
		
		// Find the selected location and extract company and location info
		const selectedLocationData = locations.find(loc => loc.Id == companyLocationId);
		if (selectedLocationData) {
			setSelectedCompany(selectedLocationData.CompanyId);
			setSelectedLocation(selectedLocationData.Id);
		}
	};

	const saveConfig = async () => {
		setSaving(true);
		try {
			// console.log('🔍 Saving config with values:', {
			// 	selectedCompanyLocation,
			// 	selectedCompany,
			// 	selectedLocation,
			// 	companiesCount: companies.length,
			// 	locationsCount: locations.length
			// });

			// Update the config with new camera settings and company/location
			// The useDevices hook will automatically save this to persistent storage
			const updatedConfig = { 
				...config, 
				cameras, 
				selectedCompanyLocation,
				selectedCompany,
				selectedLocation 
			};
			setConfig(updatedConfig);

			// Save company and location separately using the correct secureStorage methods
			try {
				// Save the full company object using secureStorage.setCompany() and update useAuth state
				if (selectedCompany) {
					const selectedLocationData = locations.find(loc => loc.Id == selectedCompanyLocation);
					// console.log('🔍 Selected location data:', selectedLocationData);
					if (selectedLocationData) {
						// Find the company object from the companies list
						const companyData = companies.find(comp => comp.Id == selectedLocationData.CompanyId);
						// console.log('🔍 Found company data:', companyData);
						if (companyData) {
							// Save to secureStorage
							await window.electronAPI.secureStorage.setCompany(companyData);
							// console.log('✅ Company saved to secureStorage:', companyData);
							
							// Update useAuth state so other screens get the new company immediately
							setCompany(companyData);
							// console.log('✅ Company state updated in useAuth');
						}
					}
				}

				// Save the locationId in settings using secureStorage.setSettings()
				if (window.electronAPI?.secureStorage?.setSettings) {
					const currentSettings = await window.electronAPI.secureStorage.getSettings();
					await window.electronAPI.secureStorage.setSettings({
						...currentSettings,
						selectedLocation: selectedLocation, // This is the locationId
						lastUpdated: new Date().toISOString()
					});
					// console.log('✅ Location saved to secureStorage:', selectedLocation);
				} else {
					const stored = localStorage.getItem('truckscale_settings');
					const savedSettings = stored ? JSON.parse(stored) : {};
					savedSettings.selectedLocation = selectedLocation; // This is the locationId
					savedSettings.lastUpdated = new Date().toISOString();
					localStorage.setItem('truckscale_settings', JSON.stringify(savedSettings));
					// console.log('✅ Location saved to localStorage:', selectedLocation);
				}

				
			} catch (storageError) {
				console.error('Failed to save company/location to storage:', storageError);
			}

			// Connect cameras if any are configured
			if (cameras.length > 0) {
				await onConnect('cameras', cameras);
			}

			alert('Configuration saved successfully');
		} catch (error) {
			console.error('Failed to save configuration:', error);
			alert('Failed to save configuration. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const clearAllConfig = async () => {
		if (confirm('Are you sure you want to clear all saved configurations? This cannot be undone.')) {
			try {
				// Clear from secure storage
				if (window.electronAPI?.secureStorage?.clearAll) {
					await window.electronAPI.secureStorage.clearAll();
				} else {
					localStorage.removeItem('truckscale_settings');
				}
				
				// Reset state
				setCameras([]);
				setSelectedCompanyLocation('');
				setSelectedCompany('');
				setSelectedLocation('');
				setConfig({
					printer: '',
					paperWidth: '80mm',
					scaleDevice: '',
					cameras: [],
					selectedCompanyLocation: '',
					selectedCompany: '',
					selectedLocation: ''
				});
				
				alert('All configurations cleared successfully');
			} catch (error) {
				console.error('Failed to clear configurations:', error);
				alert('Failed to clear configurations. Please try again.');
			}
		}
	};

	return (
		<div className="screen">
			<div className="nav-breadcrumb">
				<a onClick={() => onNavigate('home')}>Home</a> / Configuration
			</div>

			<h1>System Configuration</h1>

			<div className="grid-2">
				{/* Printer Configuration */}
				<PrinterConfig
					config={config}
					setConfig={setConfig}
					devices={devices}
					onConnect={onConnect}
				/>

				{/* Scale Configuration */}
				<ScaleConfig
					config={config}
					setConfig={setConfig}
					devices={devices}
					onConnect={onConnect}
				/>
			</div>

			{/* Company & Location Configuration */}
			<div className="card" style={{ margin: '2rem 0' }}>
				<h3><i className="fas fa-building"></i> Company & Location Settings</h3>
				<div className="form-group">
					<label className="form-label">Select Company & Location</label>
					<select
						className="form-select"
						value={selectedCompanyLocation}
						onChange={(e) => handleCompanyLocationChange(e.target.value)}
						disabled={locationsLoading}
					>
						<option value="">Select a company and location...</option>
						{locations.map(location => (
							<option key={location.Id} value={location.Id}>
								{location.DisplayName}
							</option>
						))}
					</select>
					{locationsLoading && (
						<div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6c757d' }}>
							<i className="fas fa-spinner fa-spin"></i> Loading companies and locations...
						</div>
					)}
					{locations.length === 0 && !locationsLoading && (
						<div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6c757d' }}>
							No companies or locations found. Please check your connection or contact your administrator.
						</div>
					)}
					{selectedCompanyLocation && (
						<div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#28a745' }}>
							<i className="fas fa-check-circle"></i> Selected: {locations.find(l => l.Id === selectedCompanyLocation)?.SiteName}
						</div>
					)}
					{!selectedCompanyLocation && (
						<div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#dc3545' }}>
							<i className="fas fa-exclamation-triangle"></i> <strong>Required:</strong> Please select a company and location to continue
						</div>
					)}
				</div>
			</div>

			{/* Camera Configuration */}
			<CameraConfig
				cameras={cameras}
				setCameras={setCameras}
				onAdd={addCamera}
				onUpdate={updateCamera}
				onRemove={removeCamera}
			/>

			<div className="wizard-actions">
				<button className="btn btn-secondary" onClick={() => onNavigate('home')}>
					Back to Home
				</button>
				<button
					className="btn btn-success"
					onClick={saveConfig}
					disabled={saving || !selectedCompanyLocation}
				>
					{saving ? 'Saving...' : 'Save Configuration'}
				</button>
				<button
					className="btn btn-danger"
					onClick={clearAllConfig}
					style={{ marginLeft: 'auto' }}
				>
					<i className="fas fa-trash"></i> Clear All Config
				</button>
			</div>
		</div>
	);
};

export default ConfigScreen;